import { asyncify, each } from 'async';
import { StateCreator } from 'zustand';
import { createWithEqualityFn } from 'zustand/traditional';

import {
  getGame as restGetGame,
  getGameCollections as restGetGameCollections,
  getGameStatistics as restGetGameStatistics,
} from '../../api/Rest/game';
import { Uuid } from '../../models/Base';
import {
  Game,
  GameCollection,
  GameCollectionRepository,
  GameRepository,
  GameStatistics,
  GameStatisticsRepository,
  GameStoreAction,
  GameStoreState,
  jsonToGame,
} from '../../models/GameStore';
import {
  createMultipleStoreListFieldHook,
  createStoreListFieldHook,
  createStoreMapFieldHook,
  StoreLifecycle,
  StoreLifecycleAction,
} from '../../models/Store';

import * as State from './state';

type Store = StoreLifecycleAction & GameStoreState & GameStoreAction;

const lifecycle = new StoreLifecycle('GameStore');
lifecycle.finishHydration();

const initializer: StateCreator<Store> = (set, get) => {
  const initialize = lifecycle.createInitialize(
    () => {
      // do nothing
    },
    () => set((state) => ({ ...state })),
  );

  const shutdown = lifecycle.createShutdown(
    () => {
      // clear data
      const state = {
        gameCollections: new GameCollectionRepository(),
        games: new GameRepository(),
        // slideshows: new SlideshowRepository(),
        gameStatistics: new GameStatisticsRepository(),
        // slideshowStatistics: {},
      };
      state.gameCollections.onElementPrune = onGameCollectionPrune;
      state.games.onElementPrune = onGamePrune;
      // state.slideshows.onElementPrune = onSlideshowPrune;
      set(state);
    },
    () => set((state) => ({ ...state })),
  );

  const getGameCollections: GameStoreAction['getGameCollections'] = (
    fieldName,
  ) => {
    return State.getGameCollections(get(), fieldName);
  };

  const getGameCollection: GameStoreAction['getGameCollection'] = (
    gameCollectIonId,
  ) => State.getGameCollection(get(), gameCollectIonId);

  const fetchGameCollectionsByIds: GameStoreAction['fetchGameCollectionsByIds'] =
    async ({ fieldName, gameCollectionIds }, options) => {
      const { gameCollections, missingIds } = gameCollectionIds.reduce<{
        gameCollections: GameCollection[];
        missingIds: Uuid[];
      }>(
        (acc, gameCollectionId) => {
          const gameCollection = getGameCollection(gameCollectionId);
          if (gameCollection) {
            acc.gameCollections.push(gameCollection);
          } else {
            acc.missingIds.push(gameCollectionId);
          }
          return acc;
        },
        { gameCollections: [], missingIds: [] },
      );

      set((state) => {
        State.setGameCollections(state, fieldName, ...gameCollections);
        return { ...state };
      });

      if (missingIds.length > 0) {
        const { ok, data } = await restGetGameCollections(
          { gameCollectionIds: missingIds, limit: 20 },
          options,
        );
        if (ok) {
          set((state) => {
            const missingGameCollections = data.gameCollections.map(
              (c) => new GameCollection(c),
            );
            State.pushGameCollections(
              state,
              fieldName,
              ...missingGameCollections,
            );
            return { ...state };
          });
        }
      }
    };

  const fetchGameCollections: GameStoreAction['fetchGameCollections'] = async (
    { fieldName },
    cursor,
    options,
  ) => {
    const { ok, data } = await restGetGameCollections(
      { limit: 20, cursor },
      options,
    );
    if (ok) {
      const newCursor = data.cursor ?? null;
      set((state) => {
        const gameCollections = data.gameCollections.map(
          (c) => new GameCollection(c),
        );
        if (cursor === undefined) {
          State.setGameCollections(state, fieldName, ...gameCollections);
        } else {
          State.pushGameCollections(state, fieldName, ...gameCollections);
        }
        State.setGameCollectionCursor(state, fieldName, newCursor);
        return { ...state };
      });
      return {
        shouldFetchMore: !!newCursor && data.gameCollections.length === 0,
      };
    }
    return { shouldFetchMore: false };
  };

  const getGame: GameStoreAction['getGame'] = (gameId) =>
    State.getGame(get(), gameId);

  const fetchGame: GameStoreAction['fetchGame'] = async (
    { fieldName, gameCollectionId, gameId, hash, scope },
    options,
  ) => {
    if (!getGame(gameId)) {
      const { ok, data } = await restGetGame(
        { gameCollectionId, gameId },
        { hash, scope },
        options,
      );
      if (ok) {
        set((state) => {
          const game = jsonToGame(data.game);
          State.pushGames(state, fieldName, game);
          return { ...state };
        });
      }
    }
  };

  const getGameStatistics: GameStoreAction['getGameStatistics'] = (gameId) => {
    return State.getGameStatistics(get(), gameId);
  };

  const getGameStatisticsList: GameStoreAction['getGameStatisticsList'] = (
    fieldName,
  ) => {
    return State.getGameStatisticsList(get(), fieldName);
  };

  const fetchGameStatistics: GameStoreAction['fetchGameStatistics'] = async (
    { fieldName, gameCollectionId, gameId, scope },
    options,
  ) => {
    const { ok, data } = await restGetGameStatistics(
      { gameCollectionId, gameId },
      { scope },
      options,
    );
    if (ok) {
      set((state) => {
        State.pushGameStatistics(
          state,
          fieldName,
          new GameStatistics(data.gameStatistics),
        );
        return { ...state };
      });
    }
  };

  const fetchGameAndStatisticsByIds: GameStoreAction['fetchGameAndStatisticsByIds'] =
    async ({ fieldName, gameIds }, options) => {
      const resultGame: Game[] = [];
      const resultGameStatistics: GameStatistics[] = [];

      await each(
        gameIds,
        asyncify(
          async ({
            gameCollectionId,
            gameId,
            hash,
            scope,
          }: {
            gameCollectionId: string;
            gameId: string;
            hash: string;
            scope?: string;
          }) => {
            const getGameRespond = await restGetGame(
              { gameCollectionId, gameId },
              { hash, scope },
              options,
            );
            if (getGameRespond.ok) {
              const game = jsonToGame(getGameRespond.data.game);
              resultGame.push(game);
            }
            const getGameStatisticsRespond = await restGetGameStatistics(
              { gameCollectionId, gameId },
              { scope },
              options,
            );
            if (getGameStatisticsRespond.ok) {
              const gameStatistics = new GameStatistics(
                getGameStatisticsRespond.data.gameStatistics,
              );
              resultGameStatistics.push(gameStatistics);
            }
          },
        ),
      );

      set((state) => {
        State.pushGames(state, fieldName, ...resultGame);
        State.pushGameStatistics(state, fieldName, ...resultGameStatistics);
        return { ...state };
      });
    };

  const onGameCollectionPrune: GameStoreAction['onGameCollectionPrune'] = (
    gameCollection,
  ) => {
    set((state) => {
      state.games.clear(`gameCollection_${gameCollection.id()}`);
      state.games
        .map(`gameCollection_${gameCollection.id()}`, null)
        ?.removeListener();
      return { ...state };
    });
  };

  const onGamePrune: GameStoreAction['onGamePrune'] = () => {
    set((state) => {
      // delete state.gameStatistics;
      return { ...state };
    });
  };

  const setAlbumScrollPosition: GameStoreAction['setAlbumScrollPosition'] = (
    position,
  ) => {
    set((state) => ({
      ...state,
      albumScrollPosition: position,
    }));
  };

  const getAlbumScrollPosition: GameStoreAction['getAlbumScrollPosition'] =
    () => {
      return get().albumScrollPosition;
    };

  return {
    initialize,
    shutdown,
    gameCollections: new GameCollectionRepository(),
    games: new GameRepository(),
    gameStatistics: new GameStatisticsRepository(),
    albumScrollPosition: null,
    getGameCollections,
    getGameCollection,
    fetchGameCollections,
    fetchGameCollectionsByIds,
    getGame,
    fetchGame,
    getGameStatistics,
    getGameStatisticsList,
    fetchGameStatistics,
    fetchGameAndStatisticsByIds,
    onGameCollectionPrune,
    onGamePrune,
    setAlbumScrollPosition,
    getAlbumScrollPosition,
  };
};

export const useGameStore = createWithEqualityFn<Store>()(
  initializer,
  Object.is,
);
export const useGameStoreListField = createStoreListFieldHook(useGameStore);
export const useGameStoreMultipleListField =
  createMultipleStoreListFieldHook(useGameStore);
export const useGameStoreMapField = createStoreMapFieldHook(useGameStore);
